import React, { useState } from 'react';
import { Mail, Building, MessageCircle, Send, CheckCircle } from 'lucide-react';
import './ContactPage.scss';

export const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    inquiryType: 'general',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // API call would go here
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        inquiryType: 'general',
        subject: '',
        message: '',
      });
    } catch (err) {
      setError('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="contact-page">
        <div className="container">
          <div className="success-card">
            <div className="success-content">
              <CheckCircle size={48} />
              <h1>Message Sent!</h1>
              <p>
                Thank you for contacting us. We'll get back to you within 24
                hours.
              </p>
              <button className="btn-primary" onClick={() => setSuccess(false)}>
                Send Another Message
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-page">
      <div className="container">
        <div className="page-header">
          <h1>Get in Touch</h1>
          <p>Have a question or need help? We'd love to hear from you.</p>
        </div>

        <div className="contact-layout">
          <div className="contact-info">
            <div className="info-card">
              <div className="info-header">
                <Mail size={24} />
                <h3>General Inquiries</h3>
              </div>
              <p>For general questions, course support, and feedback.</p>
              <a href="mailto:hello@aiuniversity.com" className="contact-link">
                hello@aiuniversity.com
              </a>
            </div>

            <div className="info-card">
              <div className="info-header">
                <Building size={24} />
                <h3>Business Inquiries</h3>
              </div>
              <p>
                For partnerships, enterprise solutions, and business
                opportunities.
              </p>
              <a
                href="mailto:business@aiuniversity.com"
                className="contact-link"
              >
                business@aiuniversity.com
              </a>
            </div>

            <div className="info-card">
              <div className="info-header">
                <MessageCircle size={24} />
                <h3>Quick Response</h3>
              </div>
              <p>
                Use the contact form for the fastest response. We typically
                reply within 24 hours.
              </p>
            </div>
          </div>

          <div className="contact-form-section">
            <div className="form-card">
              <h2>Send us a Message</h2>

              {error && (
                <div className="error-alert">
                  <p>{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="inquiryType">Inquiry Type</label>
                  <select
                    id="inquiryType"
                    name="inquiryType"
                    value={formData.inquiryType}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    <option value="general">General Question</option>
                    <option value="business">Business Inquiry</option>
                    <option value="support">Technical Support</option>
                    <option value="feedback">Feedback</option>
                    <option value="partnership">Partnership</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject *</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    required
                    disabled={loading}
                  />
                </div>

                <button
                  type="submit"
                  className="submit-button"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="loading-spinner"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
